import { useCallback, useEffect, useMemo, useState } from "react";
import { type UserPrincipal } from "@repo/types/identity";
import { type AuthProviderConfig } from "@repo/configs";
import { AuthContext } from "@repo/contexts";

export const AuthProvider = ({
  children,
  config,
}: {
  children: React.ReactNode;
  config: AuthProviderConfig;
}) => {
  const [userPrincipal, setUserPrincipalInternal] =
    useState<UserPrincipal | null>(null);

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await config.getUserPrincipal();
        setUserPrincipalInternal(user);
      } finally {
        setIsReady(true);
      }
    };

    loadUser();
  }, [config]);

  const setUserPrincipal = useCallback(
    async (userInfo: UserPrincipal | null): Promise<void> => {
      setUserPrincipalInternal(userInfo);
      await config.setUserPrincipal(userInfo);
    },
    [config],
  );

  const value = useMemo(
    () => ({
      user: userPrincipal,
      setUserPrincipal,
    }),
    [userPrincipal, setUserPrincipal],
  );

  if (!isReady) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
